import React, { useState, useEffect } from 'react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const MobileMenu = () => {
  const { isMobileMenuOpen, closeMobileMenu } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const { selectedCurrency, changeCurrency } = useCurrency();

  // open/close animation
  useEffect(() => {
    if (isMobileMenuOpen) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setExpandedDropdown(null);
      handleClose()
    }
  }, [isMobileMenuOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => closeMobileMenu(), 300);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/products?search=${encodeURIComponent(searchTerm.trim())}`;
      handleClose();
    }
  };

  const handleLinkClick = () => handleClose();

  const toggleDropdown = (name) =>
    setExpandedDropdown(expandedDropdown === name ? null : name);

  const handleCurrencyChange = (currencyCode, countryCode) => {
    changeCurrency(currencyCode);
    // Store the selected country for checkout
    if (countryCode) {
      localStorage.setItem('selectedCountry', countryCode);
    }
  };

  const getCurrentCountryDisplay = () => {
    const savedCountry = localStorage.getItem('selectedCountry') || 'MK';
    
    const countryMap = {
      'MK': 'North Macedonia (MKD ден.)',
      'AL': 'Albania (EUR €)',
      'XK': 'Kosovo (EUR €)',
      'RS': 'Serbia (EUR €)'
    };
    
    return countryMap[savedCountry] || 'North Macedonia (MKD ден.)';
  };

  // alignment rules
  const leftAlignCurrency = selectedCurrency === 'EUR'; // left for EUR
  const leftAlignLanguage = ['en', 'mk'].includes(currentLanguage);          // left for these
  const leftBoxStyle = { right: 'auto' };  // panel-left + 8px
  const rightBoxStyle = { right: 0, left: 'auto' };     // default (arrow-anchored case)

  if (!isMobileMenuOpen) return null;

  return (
    <>
      <style jsx>{`
        .mobile-menu-container {
          transform: translateX(100%);
          opacity: 0;
          transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
        }
        .mobile-menu-container.open { transform: translateX(0); opacity: 1; }
        .mobile-menu-overlay { opacity: 0; transition: opacity 0.3s ease-in-out; }
        .mobile-menu-overlay.open { opacity: 0.8; }
        .dropdown-item { border-bottom: 1px solid #E5E5E5; padding: 8px 0; }
        .dropdown-item:last-child { border-bottom: none; }
        .dropdown-container { max-height: 200px; overflow-y: auto; }
        .text-13 { font-size: 13px; line-height: 1.2; }
      `}</style>

      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Overlay */}
        <div
          className={`absolute inset-0 bg-black mobile-menu-overlay ${isVisible ? 'open' : ''}`}
          onClick={handleClose}
        />

        {/* Panel */}
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div className={`relative w-[358px] mobile-menu-container ${isVisible ? 'open' : ''}`}>
            <div className="h-full flex flex-col bg-white px-2">
              {/* Header */}
              <div className="pt-2.5">
                <div className="flex justify-end items-center mb-[75px]">
                  <button onClick={handleClose} className="focus:outline-none" aria-label="Close menu">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_4006_304)">
                        <path d="M14.1745 14.1745L1.82544 1.82544" stroke="black" strokeMiterlimit="10" />
                        <path d="M1.82544 14.1745L14.1745 1.82544" stroke="black" strokeMiterlimit="10" />
                      </g>
                      <defs>
                        <clipPath id="clip0_4006_304">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Nav */}
              <div className="mb-6">
                <Link to="/" className="block text-14 font-light mb-2" onClick={handleLinkClick}>
                  {t('header.home')}
                </Link>
                <Link to="/products" className="block text-14 font-medium" onClick={handleLinkClick}>
                  {t('header.products')}
                </Link>
              </div>

              {/* Search */}
              <form onSubmit={handleSearchSubmit} className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pt-[9px] pb-[8px] pl-[9px] pr-10 border border-black focus:outline-none text-14 font-regular placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal"
                  />
                  <button
                    type="submit"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_4006_309)">
                        <path d="M6.909 12.7272C10.1223 12.7272 12.7272 10.1223 12.7272 6.909C12.7272 3.69571 10.1223 1.09082 6.909 1.09082C3.69571 1.09082 1.09082 3.69571 1.09082 6.909C1.09082 10.1223 3.69571 12.7272 6.909 12.7272Z" stroke="black" strokeMiterlimit="10" />
                        <path d="M11.2727 11.2729L14.9091 14.9093" stroke="black" strokeMiterlimit="10" />
                      </g>
                      <defs>
                        <clipPath id="clip0_4006_309">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
              </form>

              {/* Country/Currency - All countries use EUR */}
              <div className="mb-2 relative">
                {/* trigger */}
                <div
                  className="flex items-center cursor-pointer text-14 font-light py-1"
                  onClick={() => toggleDropdown('currency')}
                >
                  <span className="mr-[6px]">{getCurrentCountryDisplay()}</span>

                  <div className="relative">
                    {expandedDropdown === 'currency' ? (
                      // Arrow Up
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_10003_25)">
                          <path d="M11.182 8.4545L6.00018 3.54541L0.818359 8.4545" stroke="black" strokeMiterlimit="10"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_10003_25">
                            <rect width="12" height="12" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      // Arrow Down
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_10002_124)">
                          <path d="M0.818359 3.54541L6.00018 8.4545L11.182 3.54541" stroke="black" strokeMiterlimit="10"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_10002_124">
                            <rect width="12" height="12" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    )}

                    {/* MKD → arrow-anchored */}
                    {expandedDropdown === 'currency' && !leftAlignCurrency && (
                      <div
                        className="absolute top-full mt-2 z-50 bg-white border border-black"
                        style={{ width: '200px', paddingLeft: '8px', ...rightBoxStyle }}
                      >
                        <div className="pt-2">
                          <div className={`cursor-pointer text-14 pr-2 font-light`}
                            onClick={() => { handleCurrencyChange('EUR', 'AL'); setExpandedDropdown(null); }}>
                            Albania (EUR €)
                          </div>
                          <div className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                            onClick={() => { handleCurrencyChange('EUR', 'XK'); setExpandedDropdown(null); }}>
                            Kosovo (EUR €)
                          </div>
                          <div className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                            onClick={() => { handleCurrencyChange('MKD', 'MK'); setExpandedDropdown(null); }}>
                            North Macedonia (MKD ден.)
                          </div>
                          <div className={`cursor-pointer text-14 pr-2 pt-2 pb-2 font-light`}
                            onClick={() => { handleCurrencyChange('EUR', 'RS'); setExpandedDropdown(null); }}>
                            Serbia (EUR €)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {expandedDropdown === 'currency' && leftAlignCurrency && (
                  <div
                    className="absolute top-full mt-2 z-50 bg-white border border-black"
                    style={{ width: '200px', paddingLeft: '8px', ...leftBoxStyle }}
                  >
                    <div className="pt-2">
                      <div className={`cursor-pointer text-14 pr-2 font-light`}
                        onClick={() => { handleCurrencyChange('EUR', 'AL'); setExpandedDropdown(null); }}>
                        Albania (EUR €)
                      </div>
                      <div className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                        onClick={() => { handleCurrencyChange('EUR', 'XK'); setExpandedDropdown(null); }}>
                        Kosovo (EUR €)
                      </div>
                      <div className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                        onClick={() => { handleCurrencyChange('MKD', 'MK'); setExpandedDropdown(null); }}>
                        North Macedonia (MKD ден.)
                      </div>
                      <div className={`cursor-pointer text-14 pr-2 pt-2 pb-2 font-light`}
                        onClick={() => { handleCurrencyChange('EUR', 'RS'); setExpandedDropdown(null); }}>
                        Serbia (EUR €)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Language */}
              <div className="mb-2 relative">
                <div
                  className="flex items-center cursor-pointer text-14 font-light py-1"
                  onClick={() => toggleDropdown('language')}
                >
                  <span className="mr-[6px]">{currentLanguage === 'mk' ? 'Македонски' : 'English'}</span>
                  <div className="relative">
                    {expandedDropdown === 'language' ? (
                      // Arrow Up
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_10003_25)">
                          <path d="M11.182 8.4545L6.00018 3.54541L0.818359 8.4545" stroke="black" strokeMiterlimit="10"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_10003_25">
                            <rect width="12" height="12" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      // Arrow Down
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_10002_124)">
                          <path d="M0.818359 3.54541L6.00018 8.4545L11.182 3.54541" stroke="black" strokeMiterlimit="10"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_10002_124">
                            <rect width="12" height="12" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    )}

                    {expandedDropdown === 'language' && !leftAlignLanguage && (
                      <div
                        className="absolute top-full mt-2 z-50 bg-white border border-black"
                        style={{ width: '171px', paddingLeft: '8px', ...rightBoxStyle }}
                      >
                        <div className="pt-2">
                          {availableLanguages.map((lang, index) => (
                            <div
                              key={lang.code}
                              className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                              onClick={() => { changeLanguage(lang.code); setExpandedDropdown(null); handleClose() }}
                            >
                              {lang.nativeName}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {expandedDropdown === 'language' && leftAlignLanguage && (
                  <div
                    className="absolute top-full mt-2 z-50 bg-white border border-black"
                    style={{ width: '171px', paddingLeft: '8px', ...leftBoxStyle }}
                  >
                    <div className="pb-2">
                      {availableLanguages.map((lang, index) => (
                        <div
                          key={lang.code}
                          className={`cursor-pointer text-14 pr-2 pt-2 font-light`}
                          onClick={() => { changeLanguage(lang.code); setExpandedDropdown(null); handleClose()}}
                        >
                          {lang.nativeName}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
